import React, { useState, useEffect, useCallback } from 'react';
import { Container, Paper, Typography, Box, Chip, Grid, CircularProgress, ToggleButton, ToggleButtonGroup } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid, Legend } from 'recharts';
import io from 'socket.io-client';

const SOCKET_URL = process.env.REACT_APP_SOCKET_URL || 'https://pochelo.online';
const COLORES_FASE = { A: '#e53935', B: '#fbc02d', C: '#1e88e5' };

const num = (v, dec = 1) => (v === null || v === undefined ? '—' : Number(v).toLocaleString('es-CO', { minimumFractionDigits: dec, maximumFractionDigits: dec }));
const kwh = (wh) => (wh === null || wh === undefined ? '—' : num(wh / 1000, 2));

const Dato = ({ etiqueta, valor, unidad }) => (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5 }}>
        <Typography variant="body2" color="text.secondary">{etiqueta}</Typography>
        <Typography variant="body1" sx={{ fontWeight: 500 }}>{valor} <Typography component="span" variant="caption" color="text.secondary">{unidad}</Typography></Typography>
    </Box>
);

const EnergiaShelly = () => {
    const theme = useTheme();
    const [actual, setActual] = useState(null);
    const [historico, setHistorico] = useState([]);
    const [horas, setHoras] = useState(6);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    useEffect(() => {
        fetch('/api/energia/actual')
            .then(res => res.ok ? res.json() : Promise.reject(new Error(res.status === 404 ? 'Aún no se han recibido datos del medidor' : 'Error del servidor')))
            .then(setActual)
            .catch(err => setError(err.message))
            .finally(() => setLoading(false));

        const socket = io(SOCKET_URL);
        socket.on('energia', (d) => { setActual(d); setError(''); });
        return () => socket.disconnect();
    }, []);

    const cargarHistorico = useCallback(() => {
        fetch(`/api/energia/historico?horas=${horas}`)
            .then(res => res.ok ? res.json() : [])
            .then(datos => setHistorico(datos.map(d => ({
                hora: new Date(d.fecha).toLocaleTimeString('es-CO', { hour: '2-digit', minute: '2-digit' }),
                Total: d.potenciaTotal, A: d.a?.potencia, B: d.b?.potencia, C: d.c?.potencia
            }))))
            .catch(() => setHistorico([]));
    }, [horas]);

    useEffect(() => {
        cargarHistorico();
        const t = setInterval(cargarHistorico, 60000);
        return () => clearInterval(t);
    }, [cargarHistorico]);

    if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}><CircularProgress /></Box>;

    return (
        <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                <Typography variant="h5">Consumo de energía</Typography>
                {actual && (
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                        <Typography variant="caption" color="text.secondary">
                            {actual.dispositivo} · {new Date(actual.actualizado).toLocaleString('es-CO')}
                        </Typography>
                        <Chip size="small" label={actual.conectado ? 'En línea' : 'Desconectado'} color={actual.conectado ? 'success' : 'default'} />
                    </Box>
                )}
            </Box>

            {error && !actual && <Paper sx={{ p: 3 }}><Typography color="text.secondary">{error}</Typography></Paper>}

            {actual && (
                <>
                    <Grid container spacing={2} sx={{ mb: 2 }}>
                        <Grid item xs={12} md={6}>
                            <Paper sx={{ p: 3, height: '100%' }}>
                                <Typography variant="overline" color="text.secondary">Potencia total</Typography>
                                <Typography variant="h3" sx={{ fontWeight: 600 }}>
                                    {num(actual.potenciaTotal, 0)} <Typography component="span" variant="h6" color="text.secondary">W</Typography>
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    Aparente {num(actual.potenciaAparenteTotal, 0)} VA · Corriente {num(actual.corrienteTotal, 2)} A
                                    {actual.corrienteNeutro !== null && ` · Neutro ${num(actual.corrienteNeutro, 2)} A`}
                                </Typography>
                            </Paper>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <Paper sx={{ p: 3, height: '100%' }}>
                                <Typography variant="overline" color="text.secondary">Energía consumida</Typography>
                                <Typography variant="h3" sx={{ fontWeight: 600 }}>
                                    {kwh(actual.energiaTotal)} <Typography component="span" variant="h6" color="text.secondary">kWh</Typography>
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    A {kwh(actual.fases[0].energia)} · B {kwh(actual.fases[1].energia)} · C {kwh(actual.fases[2].energia)} kWh
                                    {actual.energiaRetornada ? ` · Retornada ${kwh(actual.energiaRetornada)} kWh` : ''}
                                </Typography>
                            </Paper>
                        </Grid>
                    </Grid>

                    <Grid container spacing={2} sx={{ mb: 2 }}>
                        {actual.fases.map(f => (
                            <Grid item xs={12} sm={4} key={f.fase}>
                                <Paper sx={{ p: 2, borderTop: `4px solid ${COLORES_FASE[f.fase]}` }}>
                                    <Typography variant="h6" sx={{ mb: 1 }}>Fase {f.fase}</Typography>
                                    <Dato etiqueta="Voltaje" valor={num(f.voltaje)} unidad="V" />
                                    <Dato etiqueta="Corriente" valor={num(f.corriente, 2)} unidad="A" />
                                    <Dato etiqueta="Potencia" valor={num(f.potencia, 0)} unidad="W" />
                                    <Dato etiqueta="Factor de potencia" valor={num(f.fp, 2)} unidad="" />
                                    <Dato etiqueta="Frecuencia" valor={num(f.frecuencia)} unidad="Hz" />
                                </Paper>
                            </Grid>
                        ))}
                    </Grid>

                    {actual.errores?.length > 0 && (
                        <Paper sx={{ p: 2, mb: 2 }}>
                            <Typography color="error">Errores del medidor: {actual.errores.join(', ')}</Typography>
                        </Paper>
                    )}
                </>
            )}

            <Paper sx={{ p: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1, flexWrap: 'wrap', gap: 1 }}>
                    <Typography variant="h6">Potencia por minuto</Typography>
                    <ToggleButtonGroup size="small" exclusive value={horas} onChange={(e, v) => v && setHoras(v)}>
                        <ToggleButton value={1}>1 h</ToggleButton>
                        <ToggleButton value={6}>6 h</ToggleButton>
                        <ToggleButton value={24}>24 h</ToggleButton>
                    </ToggleButtonGroup>
                </Box>
                {historico.length === 0 ? (
                    <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>Sin histórico en este rango.</Typography>
                ) : (
                    <ResponsiveContainer width="100%" height={300}>
                        <LineChart data={historico}>
                            <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} />
                            <XAxis dataKey="hora" minTickGap={40} tick={{ fill: theme.palette.text.secondary, fontSize: 12 }} />
                            <YAxis unit=" W" tick={{ fill: theme.palette.text.secondary, fontSize: 12 }} />
                            <Tooltip contentStyle={{ background: theme.palette.background.paper, border: `1px solid ${theme.palette.divider}` }} />
                            <Legend />
                            <Line type="monotone" dataKey="Total" stroke={theme.palette.text.primary} dot={false} strokeWidth={2} />
                            <Line type="monotone" dataKey="A" stroke={COLORES_FASE.A} dot={false} />
                            <Line type="monotone" dataKey="B" stroke={COLORES_FASE.B} dot={false} />
                            <Line type="monotone" dataKey="C" stroke={COLORES_FASE.C} dot={false} />
                        </LineChart>
                    </ResponsiveContainer>
                )}
            </Paper>
        </Container>
    );
};

export default EnergiaShelly;
